/**
 * useAddressChange — builds the CustMod + per-account AcctMod cascade for a
 * CIF-level address change (legacy "Change of Contact" + "Address Verification"
 * procedures).
 *
 * Flow:
 *   1. CustMod with the new StreetAddr1/City/State/Zip + optional Addr2 fields.
 *   2. Per selected cascade account → acctMod with Fields: { Addr1, City,
 *      State, Zip, Addr2 } so Core Director's account-level address tracks the
 *      CIF change.
 *   3. CustMsgAdd audit note (via /jxrl proxy) recording verification method +
 *      who authorized the change (full name, CHG-0186).
 *   4. Session events: cif.address.changed (always), acct.address.cascaded
 *      (per cascaded account), cif.field.changed.
 *
 * Returns a run() that takes the form state and performs the cascade. Hook is
 * stateless — the dialog owns form state.
 */

import { useCallback } from 'react';
import type { CustModRequest, AcctModRequest } from '@retail-ops/shared-types';
import type { AcctType } from '@retail-ops/shared-types/enums';
import { useApi } from '../../hooks/useApi';
import { useEmitEvent } from '../../hooks/useEmitEvent';
import type { MaintActionResult } from '../types';

export interface AddressChangeInput {
  cifId: string;
  newAddr: string;
  newAddr2?: string;
  newCity: string;
  newState: string;
  newZip: string;
  verificationCode: string;
  verificationLabel: string;
  /** "Authorized by": full name of the customer who authorized the change, as typed (CHG-0186). */
  customerInitials: string;
  officerCode: string;
  cascadeAccounts: ReadonlyArray<{ acctId: string; acctType: AcctType | string }>;
  /** Optional: flag whether state changed (out-of-state move triggers a task). */
  outOfState?: boolean;
}

export interface UseAddressChangeApi {
  run: (input: AddressChangeInput) => Promise<MaintActionResult>;
}

export function useAddressChange(): UseAddressChangeApi {
  const { client } = useApi();
  const emit = useEmitEvent();

  const run = useCallback(
    async (input: AddressChangeInput): Promise<MaintActionResult> => {
      // --------------------------------------------------------------------
      // 1. CustMod — CIF-level address
      // --------------------------------------------------------------------
      const custMod: Partial<CustModRequest> & Record<string, unknown> = {
        CustId: input.cifId,
        StreetAddr1: input.newAddr,
        City: input.newCity,
        State: input.newState,
        Zip: input.newZip,
        ...(input.newAddr2 ? { StreetAddr2: input.newAddr2 } : {}),
        OffCode: input.officerCode,
      };

      try {
        const custModResp = await client.accounts.custMod(custMod as CustModRequest);
        const envelope = custModResp as unknown as { success?: boolean; error?: string };
        if (envelope?.success === false) {
          return { ok: false, error: envelope.error ?? 'CustMod failed' };
        }

        // ------------------------------------------------------------------
        // 2. Per-account cascade
        // ------------------------------------------------------------------
        let cascaded = 0;
        for (const a of input.cascadeAccounts) {
          const req: AcctModRequest = {
            AcctId: a.acctId,
            AcctType: a.acctType as AcctType,
            Fields: {
              Addr1: input.newAddr,
              City: input.newCity,
              State: input.newState,
              Zip: input.newZip,
              ...(input.newAddr2 ? { Addr2: input.newAddr2 } : {}),
            },
            OperatorNumber: input.officerCode,
          };
          try {
            await client.accounts.acctMod(req);
            cascaded += 1;
            emit({
              eventType: 'acct.address.cascaded',
              eventCategory: 'cif-account',
              payload: { cifId: input.cifId, acctId: a.acctId, acctType: a.acctType },
            });
          } catch {
            // Non-fatal — log but continue. CustMod already landed.
          }
        }

        // ------------------------------------------------------------------
        // 3. Audit note via CustMsgAdd (jxrl passthrough)
        // ------------------------------------------------------------------
        const today = new Date().toISOString().slice(0, 10);
        const auditNote =
          `Address change on ${today} ver by ${input.officerCode} via ` +
          `${input.verificationLabel}` +
          (input.customerInitials.trim() ? ` (authorized by ${input.customerInitials.trim()})` : '') +
          (input.cascadeAccounts.length > 0
            ? ` — cascaded to ${input.cascadeAccounts.length} acct(s)`
            : '');
        try {
          await client.jxrl.post('Customer/CustMsgAdd', {
            CustId: input.cifId,
            MsgType: 'AN',
            Msg: auditNote,
            OperatorNumber: input.officerCode,
          });
        } catch {
          /* non-fatal audit-note failure */
        }

        // ------------------------------------------------------------------
        // 4. Session events
        // ------------------------------------------------------------------
        emit({
          eventType: 'cif.address.changed',
          eventCategory: 'cif-account',
          payload: {
            cifId: input.cifId,
            cascadeCount: cascaded,
            verificationCode: input.verificationCode,
            outOfState: Boolean(input.outOfState),
          },
        });
        emit({
          eventType: 'cif.field.changed',
          eventCategory: 'cif-account',
          payload: {
            cifId: input.cifId,
            fields: ['StreetAddr1', 'City', 'State', 'Zip'],
          },
        });

        // ------------------------------------------------------------------
        // 5. Auto-create a compliance task on out-of-state moves
        // ------------------------------------------------------------------
        if (input.outOfState) {
          try {
            await client.retail.tasks.create({
              title: 'Address Change — pending supervisor review (out-of-state)',
              type: 'address-change-review',
              category: 'compliance',
              priority: 'High',
              assignedTo: input.officerCode,
              cifId: input.cifId,
              severity: 'warn',
              autoCreated: true,
              description:
                `CIF ${input.cifId} moved to ${input.newState}. ` +
                `Review CIP re-verification requirements.`,
            });
          } catch {
            /* non-fatal */
          }
        }

        // TODO: wire disclosure packet when Team 2's engine lands
        //       (Change-of-Contact form + updated signature card).

        return {
          ok: true,
          message:
            `Address saved to CIF` +
            (cascaded > 0 ? ` and cascaded to ${cascaded} account(s).` : '.'),
        };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    },
    [client, emit],
  );

  return { run };
}
